import { Component } from "@theme/component";
import {
  getPreferredFreeGiftSelection,
  setStoredSelectedFreeGiftVariantId,
} from "@theme/free-gift-preferences";
import {
  getRechargeDynamicBundleCartItems,
  getRechargeBundleGroupIds,
  getRechargeBundleLineKeysToRemove,
  normalizeSellingPlanId,
  serializeRechargeBundleCartMutation,
  stripRechargeBundleProperties,
} from "@theme/recharge-dynamic-bundle";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  getCachedCart,
  getVoucherDiscountCodesForHandle,
  isFullCart,
  persistVoucherCartAttribute,
  setCachedCart,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #pendingActionElement = null;
  #pendingActionScope = "";
  #voucherContextReadyEvent = "gc:voucher-context-ready";
  #freeGiftMutationObserver = null;
  #freeGiftFilterFrame = 0;
  #freeGiftResizeObserver = null;
  #freeGiftDragState = null;
  #freeGiftSuppressClickUntil = 0;
  #voucherReconciliationPromise = null;
  #lastVoucherReconciliationKey = "";
  #cartVoucherContext = null;
  #didCartVoucherContextChange = false;
  #freeGiftIntegrityCleanupPromise = null;
  #allowNextCartFormSubmit = false;
  #checkoutSanitizeInFlight = false;

  /**
   * Parses a collection-id payload from DOM data or hidden line-item properties.
   * @param {string | string[] | null | undefined} value
   * @returns {string[]}
   */
  #parseCollectionIds(value) {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean);
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      return [String(value)];
    }

    if (typeof value !== "string" || !value.trim()) return [];

    try {
      const parsed = JSON.parse(value);
      return this.#parseCollectionIds(parsed);
    } catch (_error) {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  /**
   * Normalizes a selling-plan name for comparisons.
   * @param {string | undefined | null} value
   * @returns {string}
   */
  #normalizePlanName(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  #normalizeVoucherTag(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  #normalizeVoucherList(value) {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.#normalizeVoucherTag(item))
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) return [];

      try {
        return this.#normalizeVoucherList(JSON.parse(trimmedValue));
      } catch (_error) {
        return trimmedValue
          .split(",")
          .map((item) => this.#normalizeVoucherTag(item))
          .filter(Boolean);
      }
    }

    return [];
  }

  #readFreeGiftVoucherList(card) {
    if (!(card instanceof HTMLElement)) return [];

    const rawValue = card.dataset.cartFreeGiftVouchers;
    if (!rawValue) return [];

    try {
      return this.#normalizeVoucherList(JSON.parse(rawValue));
    } catch (_error) {
      return this.#normalizeVoucherList(rawValue);
    }
  }

  async #fetchCartSnapshot() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Unable to load the current cart.");
    }

    const cart = await response.json();
    // Feed the shared cache so listeners woken by the mutation read this cart instead of
    // issuing their own /cart.js for the same data.
    setCachedCart(cart);
    this.#syncVoucherContextFromCart(cart);
    return cart;
  }

  #getConfiguredFreeGiftCatalogEntries() {
    return Array.isArray(Theme?.shopifyStorefront?.freeGiftProducts)
      ? Theme.shopifyStorefront.freeGiftProducts.filter(Boolean)
      : [];
  }

  #isConfiguredFreeGiftCatalogItem(item) {
    const configuredEntries = this.#getConfiguredFreeGiftCatalogEntries();
    if (!configuredEntries.length) return false;

    const variantId = String(
      item?.variant_id ?? item?.variantId ?? item?.id ?? "",
    ).trim();
    const productId = String(item?.product_id ?? item?.productId ?? "").trim();

    return configuredEntries.some((entry) => {
      const configuredVariantId = String(entry?.variantId || "").trim();
      const configuredProductId = String(entry?.productId || "").trim();

      return (
        (configuredVariantId && configuredVariantId === variantId) ||
        (configuredProductId && configuredProductId === productId)
      );
    });
  }

  #isFreeGiftCartItem(item) {
    return item?.properties?._gc_subscription_free_gift === "true";
  }

  #isTrustedBundleFreeGiftCartItem(item) {
    return (
      this.#isFreeGiftCartItem(item) && this.#isManagedRechargeBundleCartItem(item)
    );
  }

  #isManagedRechargeBundleCartItem(item) {
    return (
      Boolean(item?.properties?._rc_bundle) ||
      String(item?.properties?._rc_bundle_parent || "").trim() ===
        "goodchop-subscription-box"
    );
  }

  #isSubscribedCartItem(item) {
    return (
      Boolean(item?.selling_plan_allocation) ||
      this.#isManagedRechargeBundleCartItem(item)
    );
  }

  #getSubscribedCartItemsFromSnapshot(cart) {
    return Array.isArray(cart?.items)
      ? cart.items.filter((item) => this.#isSubscribedCartItem(item))
      : [];
  }

  #getSubscriptionCoreItemsFromSnapshot(cart) {
    return this.#getSubscribedCartItemsFromSnapshot(cart).filter(
      (item) => !this.#isFreeGiftCartItem(item),
    );
  }

  #getCurrentFreeGiftCartItemFromSnapshot(cart) {
    return (
      this.#getSubscribedCartItemsFromSnapshot(cart).find((item) =>
        this.#isFreeGiftCartItem(item),
      ) || null
    );
  }

  #getSuspiciousFreeGiftLineKeys(cart) {
    if (!Array.isArray(cart?.items) || !cart.items.length) return [];

    const hasTrustedSubscriptionCoreItems = cart.items.some(
      (item) =>
        this.#isSubscribedCartItem(item) &&
        !this.#isConfiguredFreeGiftCatalogItem(item),
    );

    return cart.items
      .filter((item) => {
        if (!this.#isConfiguredFreeGiftCatalogItem(item)) return false;

        if (!this.#isTrustedBundleFreeGiftCartItem(item)) {
          return true;
        }

        return !hasTrustedSubscriptionCoreItems;
      })
      .map((item) => String(item?.key || "").trim())
      .filter(Boolean);
  }

  /**
   * @param {Object} [knownCart] - Post-mutation cart from the triggering event. When present
   *   it is already authoritative, so we skip the extra /cart.js round trip.
   */
  async #sanitizeSuspiciousFreeGiftCartItems(knownCart) {
    if (!this.#getConfiguredFreeGiftCatalogEntries().length) {
      return {
        hasSuspiciousItems: false,
        didRemove: false,
      };
    }

    // Must be a full cart — free-gift integrity is judged against every line, so a
    // partial mutation response could mis-detect.
    const cart = isFullCart(knownCart)
      ? knownCart
      : await this.#fetchCartSnapshot();
    const suspiciousLineKeys = this.#getSuspiciousFreeGiftLineKeys(cart);

    if (!suspiciousLineKeys.length) {
      return {
        hasSuspiciousItems: false,
        didRemove: false,
      };
    }

    const updates = {};
    suspiciousLineKeys.forEach((key) => {
      updates[key] = 0;
    });

    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig("json", {
        body: JSON.stringify({
          updates,
          ...this.#getSectionsPayload(),
        }),
      }),
    );
    const parsedResponseText = await response.json();

    if (!response.ok || parsedResponseText.status || parsedResponseText.errors) {
      throw new Error(
        parsedResponseText.description ||
          parsedResponseText.message ||
          "Failed to remove suspicious free gift cart items.",
      );
    }

    this.#applyCartResponse(parsedResponseText);

    return {
      hasSuspiciousItems: true,
      didRemove: true,
    };
  }

  /**
   * @param {Object} [knownCart] - Cart from the triggering event, if available.
   */
  async #removeSuspiciousFreeGiftCartItems(knownCart) {
    if (this.#freeGiftIntegrityCleanupPromise) {
      return this.#freeGiftIntegrityCleanupPromise;
    }

    this.#freeGiftIntegrityCleanupPromise = (async () => {
      try {
        const { didRemove } =
          await this.#sanitizeSuspiciousFreeGiftCartItems(knownCart);
        return didRemove;
      } catch (error) {
        console.error(
          "Failed to remove suspicious free gift cart items.",
          error,
        );
        return false;
      } finally {
        this.#freeGiftIntegrityCleanupPromise = null;
      }
    })();

    return this.#freeGiftIntegrityCleanupPromise;
  }

  #getCartItemSellingPlanId(item, fallbackSellingPlanId = "") {
    return String(
      item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id ||
        item?.selling_plan_allocation?.id ||
        normalizeSellingPlanId(fallbackSellingPlanId) ||
        "",
    ).trim();
  }

  #getSelectionCollectionId(selection) {
    const directCollectionId = String(selection?.collectionId || "").trim();
    if (directCollectionId) return directCollectionId;

    return this.#parseCollectionIds(selection?.collectionIds)[0] || "";
  }

  #getBundleSelectionMergeKey(selection) {
    const productId = String(selection?.productId || "").trim();
    const variantId = String(selection?.variantId || "").trim();
    const collectionId = this.#getSelectionCollectionId(selection);

    if (!productId || !variantId || !collectionId) return "";

    return [
      selection?.isFreeGift === true ? "gift" : "core",
      productId,
      variantId,
      collectionId,
    ].join("::");
  }

  #mergeBundleSelections(selections) {
    const mergedSelections = new Map();

    selections.forEach((selection) => {
      if (!selection) return;

      const mergeKey = this.#getBundleSelectionMergeKey(selection);
      if (!mergeKey) return;

      const existingSelection = mergedSelections.get(mergeKey);
      if (existingSelection) {
        existingSelection.quantity =
          Number(existingSelection.quantity || 0) +
          Number(selection.quantity || 0);
        return;
      }

      mergedSelections.set(mergeKey, {
        ...selection,
        quantity: Number(selection.quantity || 0),
      });
    });

    return Array.from(mergedSelections.values());
  }

  #buildSelectionFromCartItem(item, fallbackSellingPlanId = "") {
    const productId = String(item?.product_id || "").trim();
    const variantId = String(item?.variant_id || "").trim();
    const quantity = Number(item?.quantity || 0);
    const sellingPlan = this.#getCartItemSellingPlanId(
      item,
      fallbackSellingPlanId,
    );
    const properties =
      item && typeof item.properties === "object" && item.properties
        ? item.properties
        : {};
    const collectionIds = this.#parseCollectionIds(
      properties._gc_product_collection_ids ||
        properties._rc_bundle_collection_id ||
        [],
    );
    const collectionId = String(
      properties._rc_bundle_collection_id || collectionIds[0] || "",
    ).trim();

    if (
      !productId ||
      !variantId ||
      !quantity ||
      !sellingPlan ||
      !collectionId
    ) {
      return null;
    }

    return {
      productId,
      variantId,
      quantity,
      sellingPlan,
      collectionId,
      collectionIds,
      isFreeGift: this.#isFreeGiftCartItem(item),
      properties,
    };
  }

  #getComparableCartItemProperties(properties = {}) {
    const sanitizedProperties = stripRechargeBundleProperties(properties);
    const comparableProperties = {};

    Object.keys(sanitizedProperties)
      .sort()
      .forEach((key) => {
        if (key === "fl_tag" || key === "_fl_tag") return;

        const value = sanitizedProperties[key];
        if (value == null || value === "") return;

        comparableProperties[key] = String(value);
      });

    return comparableProperties;
  }

  #getComparableCartItemIdentity(item) {
    return JSON.stringify({
      productId: String(item?.product_id || "").trim(),
      variantId: String(item?.variant_id || "").trim(),
      sellingPlanId: this.#getCartItemSellingPlanId(item),
      isFreeGift: this.#isFreeGiftCartItem(item),
      properties: this.#getComparableCartItemProperties(item?.properties || {}),
    });
  }

  #getRefreshedNonBundleLineKeysToRemove(
    refreshedCartItems,
    explicitItemsToRemove,
    nextSubscriptionBundleIds = [],
  ) {
    if (!Array.isArray(refreshedCartItems) || !refreshedCartItems.length) {
      return [];
    }

    if (
      !Array.isArray(explicitItemsToRemove) ||
      !explicitItemsToRemove.length
    ) {
      return [];
    }

    const nextSubscriptionBundleIdSet = new Set(
      nextSubscriptionBundleIds
        .map((bundleId) => String(bundleId || "").trim())
        .filter(Boolean),
    );
    const directKeyMatches = new Set(
      refreshedCartItems
        .map((item) => String(item?.key || "").trim())
        .filter(Boolean),
    );
    const candidateItemsByIdentity = new Map();

    refreshedCartItems.forEach((item) => {
      if (this.#isManagedRechargeBundleCartItem(item)) {
        const bundleId = String(item?.properties?._rc_bundle || "").trim();
        if (bundleId && nextSubscriptionBundleIdSet.has(bundleId)) {
          return;
        }
      }

      const identity = this.#getComparableCartItemIdentity(item);
      if (!candidateItemsByIdentity.has(identity)) {
        candidateItemsByIdentity.set(identity, []);
      }

      candidateItemsByIdentity.get(identity).push(item);
    });

    const lineKeysToRemove = [];

    explicitItemsToRemove.forEach((item) => {
      const existingKey = String(item?.key || "").trim();
      if (existingKey && directKeyMatches.has(existingKey)) {
        lineKeysToRemove.push(existingKey);
        return;
      }

      const identity = this.#getComparableCartItemIdentity(item);
      const matchingItems = candidateItemsByIdentity.get(identity) || [];
      let remainingQuantity = Number(item?.quantity || 0);

      while (matchingItems.length && remainingQuantity > 0) {
        const matchedItem = matchingItems.shift();
        const matchedKey = String(matchedItem?.key || "").trim();

        if (matchedKey) {
          lineKeysToRemove.push(matchedKey);
        }

        remainingQuantity -= Number(matchedItem?.quantity || 0);
      }
    });

    return [...new Set(lineKeysToRemove.filter(Boolean))];
  }

  #matchesBundleSelectionToLineData(selection, lineData) {
    return (
      selection?.isFreeGift !== true &&
      String(selection?.productId || "").trim() ===
        String(lineData?.productId || "").trim() &&
      String(selection?.variantId || "").trim() ===
        String(lineData?.variantId || "").trim() &&
      this.#getSelectionCollectionId(selection) ===
        String(
          lineData?.properties?._rc_bundle_collection_id ||
            lineData?.collectionIds?.[0] ||
            "",
        ).trim()
    );
  }

  #summarizeCartItem(item) {
    return {
      key: String(item?.key || "").trim(),
      product_id: String(item?.product_id || "").trim(),
      variant_id: String(item?.variant_id || "").trim(),
      quantity: Number(item?.quantity || 0),
      selling_plan_id: this.#getCartItemSellingPlanId(item),
      is_free_gift: this.#isFreeGiftCartItem(item),
      collection_id: String(
        item?.properties?._rc_bundle_collection_id ||
          this.#parseCollectionIds(
            item?.properties?._gc_product_collection_ids,
          )[0] ||
          "",
      ).trim(),
      discounts: Array.isArray(item?.line_level_discount_allocations)
        ? item.line_level_discount_allocations.map((allocation) =>
            String(allocation?.discount_application?.title || "").trim(),
          )
        : [],
    };
  }

  #summarizeBundleSelection(selection) {
    return {
      productId: String(selection?.productId || "").trim(),
      variantId: String(selection?.variantId || "").trim(),
      quantity: Number(selection?.quantity || 0),
      sellingPlan: String(selection?.sellingPlan || "").trim(),
      collectionId: this.#getSelectionCollectionId(selection),
      isFreeGift: selection?.isFreeGift === true,
    };
  }

  #logSubscriptionQuantityDebug(label, payload) {
    void label;
    void payload;
  }

  #parseVoucherConfigValue(rawConfig) {
    if (rawConfig && typeof rawConfig === "object") {
      return rawConfig;
    }

    const normalizedConfig = String(rawConfig || "").trim();
    if (!normalizedConfig) return null;

    try {
      return JSON.parse(normalizedConfig);
    } catch (_error) {}

    try {
      return JSON.parse(
        normalizedConfig.replace(/=>/g, ":").replace(/\bnil\b/g, "null"),
      );
    } catch (_error) {
      return null;
    }
  }

  #getVoucherMetaobjectEntries() {
    const entries = Theme?.shopifyStorefront?.voucherMetaobjectEntries;
    return entries && typeof entries === "object" ? entries : {};
  }

  #getVoucherTagFromEntry(entry) {
    const directTag = this.#normalizeVoucherTag(entry?.flTag);
    if (directTag) return directTag;

    return this.#normalizeVoucherTag(
      this.#parseVoucherConfigValue(entry?.config)?.fl_tag,
    );
  }

  #getVoucherTagFromHandle(handle) {
    const entries = this.#getVoucherMetaobjectEntries();
    const normalizedHandle = this.#normalizeVoucherTag(handle).toLowerCase();
    if (!normalizedHandle) return "";

    return this.#getVoucherTagFromEntry(entries[normalizedHandle]);
  }

  #getActiveFreeGiftVoucherTag() {
    const entries = this.#getVoucherMetaobjectEntries();
    const defaultVoucherTag = this.#getVoucherTagFromEntry(entries.default);
    const cartVoucherTag = this.#normalizeVoucherTag(
      this.#cartVoucherContext?.flTag,
    );
    const bodyVoucherTag = this.#normalizeVoucherTag(
      document.body?.getAttribute("data-voucher-fl-tag"),
    );
    const activeThemeVoucherTag = this.#normalizeVoucherTag(
      Theme?.shopifyStorefront?.activeFreeGiftVoucherTag,
    );
    const activeContextVoucherTag = this.#normalizeVoucherTag(
      Theme?.shopifyStorefront?.activeVoucherContext?.flTag ||
        Theme?.shopifyStorefront?.activeVoucherContext?.tag,
    );

    if (cartVoucherTag) {
      return cartVoucherTag;
    }

    if (bodyVoucherTag) {
      return bodyVoucherTag;
    }

    if (activeThemeVoucherTag) {
      return activeThemeVoucherTag;
    }

    if (activeContextVoucherTag) {
      return activeContextVoucherTag;
    }

    const urlHandle = this.#normalizeVoucherTag(
      new URLSearchParams(window.location.search).get("gc_campaign"),
    ).toLowerCase();

    if (urlHandle) {
      return this.#getVoucherTagFromHandle(urlHandle) || defaultVoucherTag;
    }

    const cookieContext = (() => {
      try {
        const cookies = String(document.cookie || "").split("; ");
        const match = cookies.find((cookie) =>
          cookie.startsWith("gc_voucher_context="),
        );

        if (!match) return null;

        return JSON.parse(
          decodeURIComponent(match.slice("gc_voucher_context=".length)),
        );
      } catch (_error) {
        return null;
      }
    })();
    const cookieVoucherTag =
      this.#getVoucherTagFromHandle(cookieContext?.handle) ||
      this.#normalizeVoucherTag(cookieContext?.flTag);
    if (cookieVoucherTag) return cookieVoucherTag;

    return defaultVoucherTag;
  }

  #readVoucherContextFromCookie() {
    try {
      const cookies = String(document.cookie || "").split("; ");
      const match = cookies.find((cookie) =>
        cookie.startsWith("gc_voucher_context="),
      );

      if (!match) return null;

      return JSON.parse(
        decodeURIComponent(match.slice("gc_voucher_context=".length)),
      );
    } catch (_error) {
      return null;
    }
  }

  #getResolvedVoucherConfig() {
    const activeConfig = this.#parseVoucherConfigValue(
      this.#getActiveVoucherContext()?.config,
    );
    if (activeConfig) return activeConfig;

    const cookieContext = this.#readVoucherContextFromCookie();
    const cookieConfig = this.#parseVoucherConfigValue(cookieContext?.config);
    if (cookieConfig) return cookieConfig;

    return this.#parseVoucherConfigValue(
      Theme?.shopifyStorefront?.defaultVoucherConfig,
    );
  }

  #getVoucherContextFromHandle(handle) {
    const normalizedHandle = this.#normalizeVoucherTag(handle).toLowerCase();
    if (!normalizedHandle) return null;

    const entries = this.#getVoucherMetaobjectEntries();
    const entry = entries[normalizedHandle];
    if (!entry) return null;

    const flTag = this.#getVoucherTagFromEntry(entry);
    if (!flTag) return null;

    return {
      handle: normalizedHandle,
      flTag,
      voucherName: String(entry?.voucherName || "").trim(),
      config: this.#parseVoucherConfigValue(entry?.config),
    };
  }

  #getVoucherContextFromVoucherName(voucherName) {
    const normalizedVoucherName = String(voucherName || "")
      .trim()
      .toLowerCase();
    if (!normalizedVoucherName) return null;

    const entries = this.#getVoucherMetaobjectEntries();

    for (const handle of Object.keys(entries)) {
      const context = this.#getVoucherContextFromHandle(handle);
      if (!context) continue;

      if (
        String(context.voucherName || "")
          .trim()
          .toLowerCase() === normalizedVoucherName
      ) {
        return context;
      }
    }

    return null;
  }

  #getVoucherContextFromFlTag(flTag, voucherName = "") {
    const normalizedFlTag = this.#normalizeVoucherTag(flTag);
    if (!normalizedFlTag) return null;

    const entries = this.#getVoucherMetaobjectEntries();

    for (const handle of Object.keys(entries)) {
      const context = this.#getVoucherContextFromHandle(handle);
      if (!context) continue;

      if (this.#normalizeVoucherTag(context.flTag) !== normalizedFlTag) {
        continue;
      }

      if (!voucherName) {
        return context;
      }

      const normalizedVoucherName = String(voucherName || "")
        .trim()
        .toLowerCase();
      const contextVoucherName = String(context.voucherName || "")
        .trim()
        .toLowerCase();

      if (!contextVoucherName || contextVoucherName === normalizedVoucherName) {
        return {
          ...context,
          voucherName: String(voucherName || context.voucherName || "").trim(),
        };
      }
    }

    return null;
  }

  #getVoucherContextFromDiscountCodes(discountCodes = []) {
    const normalizedDiscountCodes = this.#normalizeDiscountCodeList(
      Array.isArray(discountCodes)
        ? discountCodes.map((discount) =>
            typeof discount === "string" ? discount : discount?.code,
          )
        : [],
    );

    if (!normalizedDiscountCodes.length) return null;

    const entries = this.#getVoucherMetaobjectEntries();
    let bestMatch = null;
    let bestMatchCount = 0;

    for (const handle of Object.keys(entries)) {
      const context = this.#getVoucherContextFromHandle(handle);
      if (!context) continue;

      const voucherDiscountCodes = this.#normalizeDiscountCodeList(
        getVoucherDiscountCodesForHandle(handle),
      );
      if (!voucherDiscountCodes.length) continue;

      const matchCount = voucherDiscountCodes.filter((code) =>
        normalizedDiscountCodes.includes(code),
      ).length;

      if (matchCount > bestMatchCount) {
        bestMatch = context;
        bestMatchCount = matchCount;
      }
    }

    return bestMatchCount > 0 ? bestMatch : null;
  }

  #getVoucherContextFromCart(cart) {
    const attributes =
      cart?.attributes && typeof cart.attributes === "object"
        ? cart.attributes
        : {};
    const flTag = this.#normalizeVoucherTag(
      attributes.flTag || attributes.fl_tag || "",
    );
    const voucherName = String(
      attributes.voucherName || attributes.voucher_name || "",
    ).trim();

    const attributeContext =
      this.#getVoucherContextFromFlTag(flTag, voucherName) ||
      this.#getVoucherContextFromVoucherName(voucherName) ||
      (flTag
        ? {
            handle: "",
            flTag,
            voucherName,
            config: null,
          }
        : null);
    const discountCodeContext = this.#getVoucherContextFromDiscountCodes(
      cart?.discount_codes,
    );

    if (attributeContext?.handle) {
      return attributeContext;
    }

    if (attributeContext && discountCodeContext) {
      return {
        ...discountCodeContext,
        flTag: attributeContext.flTag || discountCodeContext.flTag,
        voucherName:
          attributeContext.voucherName || discountCodeContext.voucherName,
      };
    }

    return attributeContext || discountCodeContext;
  }

  #syncVoucherContextFromCart(cart) {
    const cartVoucherContext = this.#getVoucherContextFromCart(cart);
    if (!cartVoucherContext?.flTag) return null;

    const previousContextKey = JSON.stringify({
      handle: String(this.#cartVoucherContext?.handle || "").trim(),
      flTag: String(this.#cartVoucherContext?.flTag || "").trim(),
      voucherName: String(this.#cartVoucherContext?.voucherName || "").trim(),
    });
    const nextContextKey = JSON.stringify({
      handle: String(cartVoucherContext.handle || "").trim(),
      flTag: String(cartVoucherContext.flTag || "").trim(),
      voucherName: String(cartVoucherContext.voucherName || "").trim(),
    });
    this.#didCartVoucherContextChange = previousContextKey !== nextContextKey;

    this.#cartVoucherContext = cartVoucherContext;

    if (document.body) {
      document.body.setAttribute(
        "data-voucher-fl-tag",
        cartVoucherContext.flTag,
      );

      if (cartVoucherContext.voucherName) {
        document.body.setAttribute(
          "data-voucher-name",
          cartVoucherContext.voucherName,
        );
      } else {
        document.body.removeAttribute("data-voucher-name");
      }
    }

    if (window.Theme?.shopifyStorefront) {
      window.Theme.shopifyStorefront.activeFreeGiftVoucherTag =
        cartVoucherContext.flTag;
      window.Theme.shopifyStorefront.activeVoucherContext = {
        ...(window.Theme.shopifyStorefront.activeVoucherContext || {}),
        ...cartVoucherContext,
        tag: cartVoucherContext.flTag,
      };
    }

    return cartVoucherContext;
  }

  #getActiveVoucherContext(cart = null) {
    const cartVoucherContext = cart
      ? this.#syncVoucherContextFromCart(cart)
      : this.#cartVoucherContext;
    if (cartVoucherContext?.flTag) {
      return cartVoucherContext;
    }

    const activeContext = Theme?.shopifyStorefront?.activeVoucherContext;
    const normalizedActiveHandle = this.#normalizeVoucherTag(
      activeContext?.handle,
    ).toLowerCase();

    if (
      normalizedActiveHandle &&
      this.#normalizeVoucherTag(activeContext?.flTag)
    ) {
      return {
        handle: normalizedActiveHandle,
        flTag: this.#normalizeVoucherTag(activeContext.flTag),
        voucherName: String(activeContext?.voucherName || "").trim(),
        config: this.#parseVoucherConfigValue(activeContext?.config),
      };
    }

    const cookieContext = this.#readVoucherContextFromCookie();
    const cookieHandle = this.#normalizeVoucherTag(
      cookieContext?.handle,
    ).toLowerCase();
    if (cookieHandle) {
      return this.#getVoucherContextFromHandle(cookieHandle);
    }

    return this.#getVoucherContextFromHandle("default");
  }

  #normalizeDiscountCodeList(value) {
    return Array.from(
      new Set(
        (Array.isArray(value) ? value : [])
          .map((code) => String(code || "").trim())
          .filter(Boolean),
      ),
    ).sort();
  }

  #getCartDiscountCodes(cart) {
    return this.#normalizeDiscountCodeList(
      Array.isArray(cart?.discount_codes)
        ? cart.discount_codes.map((discount) => discount?.code)
        : [],
    );
  }

  #areStringListsEqual(left, right) {
    const normalizedLeft = this.#normalizeDiscountCodeList(left);
    const normalizedRight = this.#normalizeDiscountCodeList(right);

    return (
      normalizedLeft.length === normalizedRight.length &&
      normalizedLeft.every((value, index) => value === normalizedRight[index])
    );
  }

  #getVoucherDiscountDisplay() {
    const voucherSet = this.#getResolvedVoucherConfig()?.set;
    const rawDiscountValue = Array.isArray(voucherSet)
      ? voucherSet[0]?.discount_value
      : voucherSet?.discount_value;
    const normalizedValue = String(rawDiscountValue || "").trim();

    if (!normalizedValue) return "10%";
    if (normalizedValue.endsWith("%")) return normalizedValue;
    return `${normalizedValue}%`;
  }

  #getVoucherDiscountPercent() {
    const normalizedValue = this.#getVoucherDiscountDisplay();
    const parsedValue = Number.parseFloat(
      String(normalizedValue || "").replace(/[^0-9.]/g, ""),
    );

    return Number.isFinite(parsedValue) ? parsedValue : 10;
  }

  #formatMoney(cents, options = {}) {
    const amount = Number(cents || 0) / 100;
    const locale = document.documentElement.lang || "en-US";
    const currencyCode =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.iso_code ||
      "USD";
    const showCurrencyCode = options?.showCurrencyCode === true;

    try {
      const formattedAmount = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 2,
      }).format(amount);

      return showCurrencyCode
        ? `${formattedAmount} ${currencyCode}`
        : formattedAmount;
    } catch (_error) {
      return showCurrencyCode
        ? `$${amount.toFixed(2)} ${currencyCode}`
        : `$${amount.toFixed(2)}`;
    }
  }

  #applyVoucherDrivenCartBenefits() {
    const discountDisplay = this.#getVoucherDiscountDisplay();

    this.querySelectorAll("[data-cart-subscription-discount]").forEach(
      (node) => {
        if(node.textContent.indexOf('{discount}') > -1) {
          node.textContent = node.textContent.replace(/\{disocunt\}|\{discount\}%/g, discountDisplay);
        }else {
          node.textContent = discountDisplay;
        }
      },
    );

    this.querySelectorAll("[data-cart-subscription-discount-copy]").forEach(
      (node) => {
        node.textContent = `Save ${discountDisplay}`;
      },
    );
  }

  #applyVoucherDrivenCartPricing() {
    const discountPercent = this.#getVoucherDiscountPercent();
    const discountMultiplier = Math.max(1 - discountPercent / 100, 0);
    let displayOriginalTotal = 0;
    let displayFinalTotal = 0;
    let subscriptionSavingsTotal = 0;
    let subtotalItemCount = 0;

    this.querySelectorAll("[data-key]").forEach((row) => {
      if (!(row instanceof HTMLElement)) return;

      const isFreeGift = row.dataset.isFreeGift === "true";
      const isSubscribed = row.dataset.isSubscribed === "true";
      const rowQuantity = Number(row.dataset.quantity || 0);
      subtotalItemCount += rowQuantity;
      const originalLinePriceCents = Number(
        row.dataset.originalLinePriceCents || 0,
      );
      const finalLinePriceCents = Number(row.dataset.finalLinePriceCents || 0);
      const baseOriginalLinePriceCents =
        originalLinePriceCents > 0
          ? originalLinePriceCents
          : finalLinePriceCents;

      let displayOriginalLinePriceCents = baseOriginalLinePriceCents;
      let displayFinalLinePriceCents = finalLinePriceCents;

      if (isSubscribed && !isFreeGift && baseOriginalLinePriceCents > 0) {
        displayFinalLinePriceCents = Math.max(
          Math.round(baseOriginalLinePriceCents * discountMultiplier),
          0,
        );
        subscriptionSavingsTotal += Math.max(
          displayOriginalLinePriceCents - displayFinalLinePriceCents,
          0,
        );
      }

      if (!isFreeGift) {
        displayOriginalTotal += displayOriginalLinePriceCents;
        displayFinalTotal += displayFinalLinePriceCents;
      }

      const priceBreakdown = row.querySelector(
        "[data-cart-subscription-price-breakdown]",
      );
      if (!(priceBreakdown instanceof HTMLElement)) return;

      const currentPriceNode = priceBreakdown.querySelector(
        "[data-cart-subscription-price-current]",
      );
      const originalPriceNode = priceBreakdown.querySelector(
        "[data-cart-subscription-price-original]",
      );

      const formattedCurrentPrice = this.#formatMoney(
        displayFinalLinePriceCents,
      );
      const formattedOriginalPrice = this.#formatMoney(
        displayOriginalLinePriceCents,
      );

      if (currentPriceNode instanceof HTMLElement) {
        currentPriceNode.textContent = formattedCurrentPrice;
        if ("value" in currentPriceNode) {
          currentPriceNode.setAttribute("value", formattedCurrentPrice);
        }
      }

      if (originalPriceNode instanceof HTMLElement) {
        originalPriceNode.textContent = formattedOriginalPrice;
      }
    });

    const displayDiscountTotal = Math.max(
      displayOriginalTotal - displayFinalTotal,
      0,
    );

    const originalTotalNode = this.querySelector(
      "[data-cart-display-original-total]",
    );
    if (originalTotalNode instanceof HTMLElement) {
      originalTotalNode.textContent = this.#formatMoney(displayOriginalTotal);
    }

    const originalTotalRow = this.querySelector(
      "[data-cart-original-total-row]",
    );
    if (originalTotalRow instanceof HTMLElement) {
      originalTotalRow.hidden = !(displayDiscountTotal > 0);
    }

    const discountTotalNode = this.querySelector(
      "[data-cart-display-discount-total]",
    );
    if (discountTotalNode instanceof HTMLElement) {
      const cartTotalDiscount = Number(
        discountTotalNode.dataset.cartTotalDiscountCents || 0,
      );
      const showCurrencyCode =
        discountTotalNode.dataset.cartCurrencyCodeEnabled === "true";
      const effectiveDiscountTotal =
        Number.isFinite(cartTotalDiscount) && cartTotalDiscount > 0
          ? cartTotalDiscount
          : 0;
      const formattedDiscountTotal = this.#formatMoney(effectiveDiscountTotal, {
        showCurrencyCode,
      });
      discountTotalNode.textContent =
        effectiveDiscountTotal > 0
          ? `-${formattedDiscountTotal}`
          : formattedDiscountTotal;
    }

    const discountSummaryBlock = this.querySelector(
      "[data-cart-summary-discount-block]",
    );
    if (discountSummaryBlock instanceof HTMLElement) {
      const cartTotalDiscount = Number(
        discountTotalNode?.dataset?.cartTotalDiscountCents || 0,
      );
      const hasDiscountRows =
        discountSummaryBlock.querySelector(".cart__summary-discounts") !== null;
      discountSummaryBlock.hidden = !(
        (Number.isFinite(cartTotalDiscount) && cartTotalDiscount > 0) ||
        hasDiscountRows
      );
    }

    const subtotalLabelNode = this.querySelector("[data-cart-subtotal-label]");
    if (subtotalLabelNode instanceof HTMLElement) {
      subtotalLabelNode.textContent = `Subtotal (${subtotalItemCount} ${
        subtotalItemCount === 1 ? "item" : "items"
      })`;
    }

    const subscriptionSavingsNode = this.querySelector(
      "[data-cart-subscription-savings]",
    );
    if (subscriptionSavingsNode instanceof HTMLElement) {
      subscriptionSavingsNode.textContent = `Save ${this.#getVoucherDiscountDisplay()}`;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    window.addEventListener(
      this.#voucherContextReadyEvent,
      this.#handleVoucherContextReady,
    );
    this.addEventListener("click", this.#handleSubscriptionActionClick);
    this.addEventListener("click", this.#handleFreeGiftCarouselClick);
    this.addEventListener("submit", this.#handleCartFormSubmit);
    this.addEventListener("change", this.#handleSubscriptionFrequencyChange);
    this.addEventListener("wheel", this.#handleFreeGiftCarouselWheel, {
      passive: false,
    });
    this.addEventListener(
      "pointerdown",
      this.#handleFreeGiftCarouselPointerDown,
    );
    this.addEventListener(
      "pointermove",
      this.#handleFreeGiftCarouselPointerMove,
    );
    this.addEventListener("pointerup", this.#handleFreeGiftCarouselPointerEnd);
    this.addEventListener(
      "pointercancel",
      this.#handleFreeGiftCarouselPointerEnd,
    );
    this.#dedupeSubscriptionFrequencyOptions();
    this.#observeFreeGiftChanges();
    this.#observeFreeGiftCarouselLayout();
    this.#scheduleFreeGiftCarouselFilter();
    this.#applyVoucherDrivenCartBenefits();
    this.#applyVoucherDrivenCartPricing();
    // Page-load integrity check: reuse the shared cart rather than issuing a third
    // /cart.js for the same data. Falls back to a fresh fetch if the cache is empty.
    void getCachedCart().then((cart) =>
      this.#removeSuspiciousFreeGiftCartItems(cart).then((didRemove) =>
        // If the sanitizer mutated the cart, the cached copy is stale — refetch.
        this.#reconcileVoucherDrivenCartState(false, didRemove ? undefined : cart),
      ),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    window.removeEventListener(
      this.#voucherContextReadyEvent,
      this.#handleVoucherContextReady,
    );
    this.removeEventListener("click", this.#handleSubscriptionActionClick);
    this.removeEventListener("click", this.#handleFreeGiftCarouselClick);
    this.removeEventListener("submit", this.#handleCartFormSubmit);
    this.removeEventListener("change", this.#handleSubscriptionFrequencyChange);
    this.removeEventListener("wheel", this.#handleFreeGiftCarouselWheel);
    this.removeEventListener(
      "pointerdown",
      this.#handleFreeGiftCarouselPointerDown,
    );
    this.removeEventListener(
      "pointermove",
      this.#handleFreeGiftCarouselPointerMove,
    );
    this.removeEventListener(
      "pointerup",
      this.#handleFreeGiftCarouselPointerEnd,
    );
    this.removeEventListener(
      "pointercancel",
      this.#handleFreeGiftCarouselPointerEnd,
    );
    this.#disconnectFreeGiftObserver();
    this.#disconnectFreeGiftResizeObserver();

    if (this.#freeGiftFilterFrame) {
      window.cancelAnimationFrame(this.#freeGiftFilterFrame);
      this.#freeGiftFilterFrame = 0;
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.#getCartRowByLine(line);
    if (
      lineItemRow instanceof HTMLElement &&
      lineItemRow.dataset.isSubscribed === "true"
    ) {
      this.#logSubscriptionQuantityDebug(
        "Using direct cart_change for subscribed line quantity update",
        {
          line,
          requestedQuantity: quantity,
          row: {
            key: lineItemRow.dataset.key || "",
            line: lineItemRow.dataset.line || "",
            productId: lineItemRow.dataset.productId || "",
            variantId: lineItemRow.dataset.variantId || "",
            isFreeGift: lineItemRow.dataset.isFreeGift === "true",
          },
        },
      );
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartRowByLine(line);
    if (!cartItemRowToRemove) return;

    // Prevent duplicate clicks
    if (cartItemRowToRemove.dataset.isRemoving === "true") return;
    cartItemRowToRemove.dataset.isRemoving = "true";

    const removeButton =
      cartItemRowToRemove instanceof HTMLElement
        ? cartItemRowToRemove.querySelector(".cart-items__remove-link")
        : null;

    // 1. Show the loader on the remove button (from your original code)
    if (!this.#pendingActionElement && removeButton instanceof HTMLElement) {
      this.#setPendingAction(removeButton, "line-item-action");
    }

    // 2. Instant visual feedback: fade out and collapse the row
    if (cartItemRowToRemove instanceof HTMLElement) {
      const currentHeight = cartItemRowToRemove.offsetHeight;
      cartItemRowToRemove.style.transition = 'opacity 0.2s ease, max-height 0.2s ease, margin 0.2s ease, padding 0.2s ease';
      cartItemRowToRemove.style.maxHeight = `${currentHeight}px`;
      void cartItemRowToRemove.offsetHeight; // force reflow
      cartItemRowToRemove.style.opacity = '0';
      cartItemRowToRemove.style.maxHeight = '0px';
      cartItemRowToRemove.style.paddingTop = '0px';
      cartItemRowToRemove.style.paddingBottom = '0px';
      cartItemRowToRemove.style.marginTop = '0px';
      cartItemRowToRemove.style.marginBottom = '0px';
      cartItemRowToRemove.style.overflow = 'hidden';
      cartItemRowToRemove.style.pointerEvents = 'none';
    }

    try {
      // 🚀 FAST PATH: Non-subscription items -> use simple cart_change (Much faster!)
      if (cartItemRowToRemove.dataset.isSubscribed !== "true") {
        await this.updateQuantity({
          line,
          quantity: 0,
          action: "clear",
        });
        return;
      }

      // 🐢 SLOW PATH: Subscription items -> full bundle sync (Necessary complexity)
      const subscribedRows = this.#getSubscribedLineRows();
      const desiredSelections = this.#appendSelectedFreeGiftSelection(
        this.#getSubscriptionCoreRows()
          .filter((row) => row.dataset.key !== cartItemRowToRemove.dataset.key)
          .map((row) => this.#createBundleSelection(this.#getLineData(row)))
          .filter(Boolean),
        {
          excludeLineKey: cartItemRowToRemove.dataset.key || "",
        },
      );

      await this.#syncSubscriptionBundle({
        desiredSelections,
        lineKeysToRemove: subscribedRows.map((row) => row.dataset.key || ""),
        loadingScope: "line-item-action",
      });
      return;

    } catch (error) {
      console.error("Failed to remove item:", error);
      
      // Revert UI on failure
      if (cartItemRowToRemove instanceof HTMLElement) {
        delete cartItemRowToRemove.dataset.isRemoving;
        cartItemRowToRemove.style.opacity = '1';
        cartItemRowToRemove.style.maxHeight = '';
        cartItemRowToRemove.style.paddingTop = '';
        cartItemRowToRemove.style.paddingBottom = '';
        cartItemRowToRemove.style.marginTop = '';
        cartItemRowToRemove.style.marginBottom = '';
        cartItemRowToRemove.style.overflow = '';
        cartItemRowToRemove.style.pointerEvents = 'auto';
      }
    } finally {
      // 3. Clear the loader from the button
      this.#clearPendingAction();
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      ...this.#getSectionsPayload(),
    });

    cartTotal?.shimmer();

    return fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig("json", { body }),
    )
      .then((response) => response.json())
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#applyCartResponse(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        this.#clearPendingAction();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    const cartItemsHtml = event.detail?.resource?.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
      this.#dedupeSubscriptionFrequencyOptions();
      this.#observeFreeGiftCarouselLayout();
      this.#scheduleFreeGiftCarouselFilter();
      this.#applyVoucherDrivenCartBenefits();
      this.#applyVoucherDrivenCartPricing();
      return;
    }

  //   sectionRenderer.renderSection(this.sectionId, { cache: false });
  // };
    sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      this.#applyVoucherDrivenCartBenefits();
      this.#applyVoucherDrivenCartPricing();
    });
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorByLine(line);
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    this.#syncVoucherContextFromCart(event?.detail?.data);
    const didVoucherContextChange = this.#didCartVoucherContextChange;
    this.#didCartVoucherContextChange = false;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
      this.#dedupeSubscriptionFrequencyOptions();
      this.#observeFreeGiftCarouselLayout();
      this.#scheduleFreeGiftCarouselFilter();
      this.#applyVoucherDrivenCartBenefits();
      this.#applyVoucherDrivenCartPricing();
      this.#syncStoredFreeGiftPreference();
    // } else {
    //   sectionRenderer.renderSection(this.sectionId, { cache: false });
    //   this.#observeFreeGiftCarouselLayout();
    //   this.#scheduleFreeGiftCarouselFilter();
    //   this.#applyVoucherDrivenCartBenefits();
    //   this.#applyVoucherDrivenCartPricing();
    // }

    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#applyVoucherDrivenCartBenefits();
        this.#applyVoucherDrivenCartPricing();
      });
      this.#observeFreeGiftCarouselLayout();
      this.#scheduleFreeGiftCarouselFilter();
    }

    if (didVoucherContextChange) {
      void this.#reconcileVoucherDrivenCartState(true);
    }

    void this.#removeSuspiciousFreeGiftCartItems(event.detail?.resource);
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems(scope = "") {
    this.classList.add("cart-items-disabled");
    this.setAttribute("aria-busy", "true");
    if (scope) {
      this.dataset.loadingScope = scope;
    } else {
      delete this.dataset.loadingScope;
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
    this.removeAttribute("aria-busy");
    delete this.dataset.loadingScope;
  }

  /**
   * Highlights the clicked cart action while the bundle sync is running.
   * @param {HTMLElement | null} element
   */
  #setPendingAction(element, scope = "") {
    this.#clearPendingAction();

    if (!(element instanceof HTMLElement)) return;

    this.#pendingActionElement = element;
    this.#pendingActionScope = scope;
    element.classList.add("is-loading");
    element.setAttribute("aria-busy", "true");
    element.setAttribute("aria-disabled", "true");
  }

  /**
   * Resets the loading state for the last pending cart action.
   */
  #clearPendingAction() {
    if (!(this.#pendingActionElement instanceof HTMLElement)) {
      this.#pendingActionElement = null;
      return;
    }

    this.#pendingActionElement.classList.remove("is-loading");
    this.#pendingActionElement.removeAttribute("aria-busy");
    this.#pendingActionElement.removeAttribute("aria-disabled");
    this.#pendingActionElement = null;
    this.#pendingActionScope = "";
  }

  #handleSubscriptionActionClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const actionButton = event.target.closest(
      "[data-cart-subscribe-all], [data-cart-switch-all-to-onetime], [data-cart-switch-to-subscription], [data-cart-switch-to-onetime]",
    );

    if (!(actionButton instanceof HTMLElement) || !this.contains(actionButton))
      return;

    event.preventDefault();
    const hasBulkSubscriptionAction =
      actionButton.hasAttribute("data-cart-subscribe-all") ||
      actionButton.hasAttribute("data-cart-switch-all-to-onetime");
    const actionLoadingScope =
      hasBulkSubscriptionAction
        ? "subscription"
        : "line-item-action";
    this.#setPendingAction(actionButton, actionLoadingScope);

    try {
      if (actionButton.hasAttribute("data-cart-subscribe-all")) {
        const oneTimeRows = this.#getEligibleOneTimeLineRows();
        const desiredSelections = [];
        const lockedPlanName = this.#getCurrentBundleSellingPlanName();

        for (const row of oneTimeRows) {
          const lineData = this.#getLineData(row);
          const preferredSellingPlan = this.#getPreferredSellingPlan(
            lineData,
            lockedPlanName,
          );

          if (!preferredSellingPlan) {
            console.error(
              "Unable to convert cart line to subscription because no selling plan matched the current cart cadence.",
              { lineData, lockedPlanName },
            );
            return;
          }

          const selection = this.#createBundleSelection(lineData, {
            sellingPlanId: preferredSellingPlan.id,
          });

          if (!selection) {
            console.error(
              "Unable to convert cart line to subscription because bundle child data is missing.",
              lineData,
            );
            return;
          }

          desiredSelections.push(selection);
        }

        await this.#syncSubscriptionBundle({
          desiredSelections:
            this.#appendSelectedFreeGiftSelection(desiredSelections),
          lineKeysToRemove: oneTimeRows.map((row) => row.dataset.key || ""),
          loadingScope: actionLoadingScope,
        });
        return;
      }

      if (actionButton.hasAttribute("data-cart-switch-all-to-onetime")) {
        const subscriptionCoreRows = this.#getSubscriptionCoreRows();
        const convertibleSubscriptionCoreRows = subscriptionCoreRows.filter(
          (subscriptionCoreRow) =>
            subscriptionCoreRow.dataset.requiresSellingPlan !== "true",
        );

        if (
          !subscriptionCoreRows.length ||
          convertibleSubscriptionCoreRows.length !==
            subscriptionCoreRows.length
        ) {
          console.error(
            "Unable to convert all subscription cart lines to one-time purchases because one or more variants require a selling plan.",
            {
              subscriptionCoreRows,
            },
          );
          return;
        }

        const oneTimeItems = convertibleSubscriptionCoreRows
          .map((subscriptionCoreRow) =>
            this.#buildOneTimeCartItem(this.#getLineData(subscriptionCoreRow)),
          )
          .filter(Boolean);

        if (oneTimeItems.length !== subscriptionCoreRows.length) {
          console.error(
            "Unable to convert all subscription cart lines to one-time purchases because one or more cart items could not be rebuilt.",
            {
              subscriptionCoreRows,
              oneTimeItems,
            },
          );
          return;
        }

        const subscribedRows = this.#getSubscribedLineRows();
        await this.#syncSubscriptionBundle({
          additionalCartItems: oneTimeItems,
          lineKeysToRemove: subscribedRows.map(
            (subscribedRow) => subscribedRow.dataset.key || "",
          ),
          loadingScope: actionLoadingScope,
        });
        return;
      }

      const row = actionButton.closest("[data-key]");
      if (!(row instanceof HTMLElement)) return;

      const lineData = this.#getLineData(row);

      if (actionButton.hasAttribute("data-cart-switch-to-onetime")) {
        const oneTimeItem = this.#buildOneTimeCartItem(lineData);
        if (!oneTimeItem) {
          console.error(
            "Unable to convert cart line to one-time purchase because this variant requires a selling plan.",
            lineData,
          );
          return;
        }

        const subscribedRows = this.#getSubscribedLineRows();
        const desiredSelections = this.#appendSelectedFreeGiftSelection(
          this.#getSubscriptionCoreRows()
            .filter((coreRow) => coreRow.dataset.key !== lineData.key)
            .map((coreRow) =>
              this.#createBundleSelection(this.#getLineData(coreRow)),
            )
            .filter(Boolean),
          {
            excludeLineKey: lineData.key,
          },
        );

        await this.#syncSubscriptionBundle({
          desiredSelections,
          additionalCartItems: [oneTimeItem],
          lineKeysToRemove: subscribedRows.map(
            (subscribedRow) => subscribedRow.dataset.key || "",
          ),
          loadingScope: actionLoadingScope,
        });
        return;
      }

      const preferredSellingPlan = this.#getPreferredSellingPlan(
        lineData,
        this.#getCurrentBundleSellingPlanName(),
      );
      if (!preferredSellingPlan) return;
      const matchingSubscribedRow = this.#getSubscriptionCoreRows().find(
        (subscribedRow) => {
          const subscribedLineData = this.#getLineData(subscribedRow);

          return (
            subscribedLineData.isFreeGift !== true &&
            subscribedLineData.variantId === lineData.variantId &&
            String(subscribedLineData.currentSellingPlanId || "") ===
              String(preferredSellingPlan.id || "")
          );
        },
      );

      if (matchingSubscribedRow instanceof HTMLElement) {
        const matchingLineData = this.#getLineData(matchingSubscribedRow);
        const response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig("json", {
            body: JSON.stringify({
              updates: {
                [matchingLineData.key]:
                  Number(matchingLineData.quantity || 0) +
                  Number(lineData.quantity || 0),
                [lineData.key]: 0,
              },
              ...this.#getSectionsPayload(),
            }),
          }),
        );
        const parsedResponseText = await response.json();

        if (
          !response.ok ||
          parsedResponseText.status ||
          parsedResponseText.errors
        ) {
          throw new Error(
            parsedResponseText.description ||
              parsedResponseText.message ||
              "Failed to update cart.",
          );
        }

        this.#applyCartResponse(parsedResponseText);
        return;
      }

      const nextSelection = this.#createBundleSelection(lineData, {
        sellingPlanId: preferredSellingPlan.id,
      });
      if (!nextSelection) {
        console.error(
          "Unable to convert cart line to subscription because bundle child data is missing.",
          lineData,
        );
        return;
      }

      const subscribedRows = this.#getSubscribedLineRows();
      const desiredSelections = this.#appendSelectedFreeGiftSelection(
        [
          ...this.#getSubscriptionCoreRows()
            .map((subscribedRow) =>
              this.#createBundleSelection(this.#getLineData(subscribedRow)),
            )
            .filter(Boolean),
          nextSelection,
        ].filter(Boolean),
      );

      await this.#syncSubscriptionBundle({
        desiredSelections,
        lineKeysToRemove: [
          ...subscribedRows.map(
            (subscribedRow) => subscribedRow.dataset.key || "",
          ),
          lineData.key,
        ],
        loadingScope: actionLoadingScope,
      });
    } finally {
      this.#clearPendingAction();
    }
  };

  #handleCartFormSubmit = async (event) => {
    if (this.#allowNextCartFormSubmit) {
      this.#allowNextCartFormSubmit = false;
      return;
    }

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "cart-form") return;

    const submitter = event.submitter;
    const isCheckoutSubmit =
      !(submitter instanceof HTMLElement) ||
      submitter.getAttribute("name") === "checkout" ||
      submitter.id === "checkout";

    if (!isCheckoutSubmit) return;

    event.preventDefault();

    if (this.#checkoutSanitizeInFlight) return;

    this.#checkoutSanitizeInFlight = true;
    this.#disableCartItems("checkout");

    if (submitter instanceof HTMLElement) {
      this.#setPendingAction(submitter, "checkout");
    }

    try {
      const existingCleanupPromise = this.#freeGiftIntegrityCleanupPromise;
      if (existingCleanupPromise) {
        const didRemoveSuspiciousItems = await existingCleanupPromise;

        if (didRemoveSuspiciousItems) {
          return;
        }
      }

      const { hasSuspiciousItems } =
        await this.#sanitizeSuspiciousFreeGiftCartItems();

      if (hasSuspiciousItems) {
        return;
      }

      this.#allowNextCartFormSubmit = true;

      if (submitter instanceof HTMLButtonElement) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    } catch (error) {
      console.error("Failed to validate cart before checkout.", error);
    } finally {
      this.#checkoutSanitizeInFlight = false;
      this.#enableCartItems();
      this.#clearPendingAction();
    }
  };

  #handleFreeGiftCarouselClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    if (Date.now() < this.#freeGiftSuppressClickUntil) {
      const suppressedButton = event.target.closest(
        "[data-cart-free-gift-select]",
      );
      if (suppressedButton && this.contains(suppressedButton)) {
        event.preventDefault();
        return;
      }
    }

    const giftButton = event.target.closest("[data-cart-free-gift-select]");
    if (giftButton instanceof HTMLButtonElement && this.contains(giftButton)) {
      event.preventDefault();
      this.#setPendingAction(giftButton, "free-gift");

      try {
        await this.#handleFreeGiftSelectionChange(giftButton);
      } finally {
        this.#clearPendingAction();
      }
      return;
    }

    const navButton = event.target.closest("[data-cart-free-gift-nav]");
    if (!(navButton instanceof HTMLButtonElement) || !this.contains(navButton))
      return;

    const wrapper = navButton.closest(".cart-items__free-gift");
    const carousel = wrapper?.querySelector("[data-cart-free-gift-carousel]");
    if (!(carousel instanceof HTMLElement)) return;

    event.preventDefault();

    const direction = navButton.dataset.cartFreeGiftNav === "prev" ? -1 : 1;
    const firstCard = carousel.querySelector(".cart-items__free-gift-card");
    const cardWidth =
      firstCard instanceof HTMLElement
        ? firstCard.getBoundingClientRect().width
        : carousel.clientWidth * 0.8;
    const styles = window.getComputedStyle(carousel);
    const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;

    carousel.scrollBy({
      left: (cardWidth + gap) * direction,
      behavior: "smooth",
    });

    window.setTimeout(() => {
      this.#updateFreeGiftCarouselNavState(carousel);
    }, 220);
  };

  #handleFreeGiftCarouselPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType !== "mouse" || event.button !== 0) return;

    const carousel = event.target.closest("[data-cart-free-gift-carousel]");
    if (!(carousel instanceof HTMLElement) || !this.contains(carousel)) return;
    if (carousel.scrollWidth <= carousel.clientWidth + 1) return;

    event.preventDefault();

    this.#freeGiftDragState = {
      carousel,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: carousel.scrollLeft,
      didDrag: false,
    };

    carousel.classList.add("is-dragging");
    carousel.setPointerCapture?.(event.pointerId);
  };

  #handleFreeGiftCarouselPointerMove = (event) => {
    const dragState = this.#freeGiftDragState;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.startX;

    if (!dragState.didDrag && Math.abs(deltaX) > 4) {
      dragState.didDrag = true;
    }

    if (!dragState.didDrag) return;

    event.preventDefault();
    dragState.carousel.scrollLeft = dragState.startScrollLeft - deltaX;
    this.#updateFreeGiftCarouselNavState(dragState.carousel);
  };

  #handleFreeGiftCarouselPointerEnd = (event) => {
    const dragState = this.#freeGiftDragState;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    dragState.carousel.classList.remove("is-dragging");
    dragState.carousel.releasePointerCapture?.(event.pointerId);

    if (dragState.didDrag) {
      this.#freeGiftSuppressClickUntil = Date.now() + 250;
    }

    this.#freeGiftDragState = null;
  };

  #handleFreeGiftCarouselWheel = (event) => {
    if (!(event.target instanceof Element)) return;

    const carousel = event.target.closest("[data-cart-free-gift-carousel]");
    if (!(carousel instanceof HTMLElement) || !this.contains(carousel)) return;
    if (carousel.scrollWidth <= carousel.clientWidth + 1) return;

    const { deltaX = 0, deltaY = 0 } = event;
    if (Math.abs(deltaY) <= Math.abs(deltaX) || deltaY === 0) return;

    event.preventDefault();
    carousel.scrollLeft += deltaY;
    this.#updateFreeGiftCarouselNavState(carousel);
  };

  #handleSubscriptionFrequencyChange = async (event) => {
    const control = event.target;
    if (!(control instanceof HTMLSelectElement)) return;

    if (!control.hasAttribute("data-cart-subscription-frequency")) return;

    const selectedPlanName = control.value;
    if (!selectedPlanName) return;

    const subscribedRows = this.#getSubscribedLineRows();
    let didChange = false;

    const desiredSelections = this.#appendSelectedFreeGiftSelection(
      this.#getSubscriptionCoreRows()
        .map((row) => {
          const lineData = this.#getLineData(row);
          const nextSellingPlan = lineData.sellingPlans.find(
            (plan) => plan.name === selectedPlanName,
          );

          if (!nextSellingPlan) {
            return this.#createBundleSelection(lineData);
          }

          if (lineData.currentSellingPlan !== nextSellingPlan.name) {
            didChange = true;
          }

          return this.#createBundleSelection(lineData, {
            sellingPlanId: nextSellingPlan.id,
          });
        })
        .filter(Boolean),
      {
        sellingPlanName: selectedPlanName,
      },
    );

    if (!didChange) return;

    await this.#syncSubscriptionBundle({
      desiredSelections,
      lineKeysToRemove: subscribedRows.map((row) => row.dataset.key || ""),
      loadingScope: "subscription",
    });
  };

  /**
   * Rebuilds the subscription bundle when the free-gift selector changes.
   * @param {HTMLButtonElement | HTMLSelectElement} control
   */
  async #handleFreeGiftSelectionChange(control) {
    const subscribedRows = this.#getSubscribedLineRows();
    const subscriptionCoreRows = this.#getSubscriptionCoreRows();
    const currentGiftRow = this.#getSelectedFreeGiftRow();
    const sellingPlanId = this.#getCurrentBundleSellingPlanId();
    const sellingPlanName = this.#getCurrentBundleSellingPlanName();
    const nextGiftSelection = this.#buildFreeGiftSelectionFromControl(control, {
      sellingPlanId,
      sellingPlanName,
    });

    if (!subscriptionCoreRows.length) return;
    if (!nextGiftSelection) {
      console.error(
        "Unable to add the selected free gift because its subscription bundle data is incomplete.",
        {
          sellingPlanName,
        },
      );
      return;
    }

    const currentGiftVariantId =
      currentGiftRow instanceof HTMLElement
        ? currentGiftRow.dataset.variantId
        : "";
    const nextGiftVariantId = nextGiftSelection?.variantId || "";

    if (currentGiftVariantId === nextGiftVariantId) return;

    setStoredSelectedFreeGiftVariantId(nextGiftVariantId);

    const desiredSelections = this.#appendSelectedFreeGiftSelection(
      subscriptionCoreRows
        .map((row) => this.#createBundleSelection(this.#getLineData(row)))
        .filter(Boolean),
      { giftSelection: nextGiftSelection },
    );

    await this.#syncSubscriptionBundle({
      desiredSelections,
      lineKeysToRemove: subscribedRows.map((row) => row.dataset.key || ""),
      loadingScope: "free-gift",
    });
  }

  /**
   * Rebuilds the subscribed cart group as a Recharge dynamic bundle.
   * @param {{
   *   desiredSelections?: Array<any>,
   *   lineKeysToRemove?: string[],
   *   additionalCartItems?: Array<Record<string, any>>
   *   loadingScope?: string
   * }} options
   */
  async #syncSubscriptionBundle(options = {}) {
    const desiredSelections = Array.isArray(options.desiredSelections)
      ? options.desiredSelections.filter(Boolean)
      : [];
    const lineKeysToRemove = Array.isArray(options.lineKeysToRemove)
      ? [...new Set(options.lineKeysToRemove.filter(Boolean))]
      : [];
    const additionalCartItems = Array.isArray(options.additionalCartItems)
      ? options.additionalCartItems.filter(Boolean)
      : [];
    const loadingScope = String(
      options.loadingScope || this.#pendingActionScope || "",
    ).trim();

    if (
      !desiredSelections.length &&
      !lineKeysToRemove.length &&
      !additionalCartItems.length
    ) {
      return;
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      "subscription-bundle-sync:user-action",
    );

    this.#disableCartItems(loadingScope);

    try {
      await serializeRechargeBundleCartMutation(async () => {
        const itemsToAdd = [...additionalCartItems];
        const isSubscriptionQuantityFlow = loadingScope === "subscription";
        const currentCartSnapshot = lineKeysToRemove.length
          ? await this.#fetchCartSnapshot()
          : null;
        const explicitItemsToRemove = Array.isArray(currentCartSnapshot?.items)
          ? currentCartSnapshot.items.filter((item) => {
              const key = String(item?.key || "").trim();
              return key && lineKeysToRemove.includes(key);
            })
          : [];
        const previousSubscriptionBundleIds = getRechargeBundleGroupIds(
          this.#getSubscribedCartItemsFromSnapshot(currentCartSnapshot),
        );
        let nextSubscriptionBundleIds = [];

        if (desiredSelections.length) {
          if (isSubscriptionQuantityFlow) {
            this.#logSubscriptionQuantityDebug(
              "Building Recharge bundle cart items",
              {
                desiredSelections: desiredSelections.map((selection) =>
                  this.#summarizeBundleSelection(selection),
                ),
                additionalCartItems,
                lineKeysToRemove,
              },
            );
          }

          const bundleItems =
            await getRechargeDynamicBundleCartItems(desiredSelections);
          itemsToAdd.push(...bundleItems);
          nextSubscriptionBundleIds = getRechargeBundleGroupIds(bundleItems);

          if (isSubscriptionQuantityFlow) {
            this.#logSubscriptionQuantityDebug(
              "Recharge bundle cart items built",
              {
                bundleItems,
              },
            );
          }
        }

        let parsedResponseText = null;

        if (itemsToAdd.length) {
          const shouldRequestSectionsOnAdd = lineKeysToRemove.length === 0;
          const addRequestPayload = {
            items: itemsToAdd,
            ...(shouldRequestSectionsOnAdd ? this.#getSectionsPayload() : {}),
          };

          if (isSubscriptionQuantityFlow) {
            this.#logSubscriptionQuantityDebug("POST cart_add payload", {
              url: Theme.routes.cart_add_url,
              payload: addRequestPayload,
            });
          }

          const addResponse = await fetch(
            Theme.routes.cart_add_url,
            fetchConfig("json", {
              body: JSON.stringify(addRequestPayload),
            }),
          );
          const addResponseJson = await addResponse.json();

          if (isSubscriptionQuantityFlow) {
            this.#logSubscriptionQuantityDebug("cart_add response", {
              ok: addResponse.ok,
              status: addResponse.status,
              response: addResponseJson,
            });
          }

          if (
            !addResponse.ok ||
            addResponseJson.status ||
            addResponseJson.errors
          ) {
            throw new Error(
              addResponseJson.description ||
                addResponseJson.message ||
                "Failed to add updated cart items.",
            );
          }

          parsedResponseText = addResponseJson;
        }

        if (lineKeysToRemove.length) {
          const refreshedCartSnapshot = await this.#fetchCartSnapshot();
          const refreshedBundleLineKeysToRemove =
            getRechargeBundleLineKeysToRemove(
              refreshedCartSnapshot?.items || [],
              previousSubscriptionBundleIds,
              nextSubscriptionBundleIds,
            );
          const refreshedNonBundleLineKeysToRemove =
            this.#getRefreshedNonBundleLineKeysToRemove(
              refreshedCartSnapshot?.items || [],
              explicitItemsToRemove.filter(
                (item) => !this.#isManagedRechargeBundleCartItem(item),
              ),
              nextSubscriptionBundleIds,
            );
          const refreshedLineKeysToRemove = [
            ...new Set([
              ...refreshedBundleLineKeysToRemove,
              ...refreshedNonBundleLineKeysToRemove,
            ]),
          ];
          /** @type {Record<string, number>} */
          const updates = {};

          refreshedLineKeysToRemove.forEach((key) => {
            updates[key] = 0;
          });

          if (Object.keys(updates).length) {
            const updateRequestPayload = {
              updates,
              ...this.#getSectionsPayload(),
            };

            if (isSubscriptionQuantityFlow) {
              this.#logSubscriptionQuantityDebug("POST cart_update payload", {
                url: Theme.routes.cart_update_url,
                payload: updateRequestPayload,
              });
            }

            const response = await fetch(
              Theme.routes.cart_update_url,
              fetchConfig("json", {
                body: JSON.stringify(updateRequestPayload),
              }),
            );

            parsedResponseText = await response.json();

            if (isSubscriptionQuantityFlow) {
              this.#logSubscriptionQuantityDebug("cart_update response", {
                ok: response.ok,
                status: response.status,
                response: parsedResponseText,
              });
            }

            if (
              !response.ok ||
              parsedResponseText.status ||
              parsedResponseText.errors
            ) {
              throw new Error(
                parsedResponseText.description ||
                  parsedResponseText.message ||
                  "Failed to update cart.",
              );
            }
          }
        }

        if (parsedResponseText) {
          if (isSubscriptionQuantityFlow) {
            this.#logSubscriptionQuantityDebug("Final cart response applied", {
              itemCount: Number(parsedResponseText?.item_count || 0),
              items: Array.isArray(parsedResponseText?.items)
                ? parsedResponseText.items.map((item) =>
                    this.#summarizeCartItem(item),
                  )
                : [],
              discountCodes: Array.isArray(parsedResponseText?.discount_codes)
                ? parsedResponseText.discount_codes
                : [],
            });
          }
          this.#applyCartResponse(parsedResponseText);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Applies a cart response and refreshes all linked cart sections.
   * @param {Record<string, any>} parsedResponseText
   */
  #applyCartResponse(parsedResponseText) {
    this.#syncVoucherContextFromCart(parsedResponseText);
    const didVoucherContextChange = this.#didCartVoucherContextChange;
    this.#didCartVoucherContextChange = false;

    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    const itemCount =
      typeof parsedResponseText.item_count === "number"
        ? parsedResponseText.item_count
        : this.#getItemCountFromSectionHtml(sectionHtml);

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount,
        source: "cart-items-component",
        sections: parsedResponseText.sections,
      }),
    );

    if (sectionHtml) {
      morphSection(this.sectionId, sectionHtml);
      this.#updateCartQuantitySelectorButtonStates();
      this.#dedupeSubscriptionFrequencyOptions();
      this.#applyVoucherDrivenCartBenefits();
      this.#applyVoucherDrivenCartPricing();
      this.#observeFreeGiftCarouselLayout();
      this.#scheduleFreeGiftCarouselFilter();
      this.#syncFreeGiftCarouselNavState();
      this.#syncStoredFreeGiftPreference();
      this.#runPostResponseCartIntegrity(
        parsedResponseText,
        didVoucherContextChange,
      );
      return;
    }

    // sectionRenderer.renderSection(this.sectionId, { cache: false });
    // this.#observeFreeGiftCarouselLayout();
    // this.#scheduleFreeGiftCarouselFilter();

    // this.#runPostResponseCartIntegrity(
    //   parsedResponseText,
    //   didVoucherContextChange,
    // );
    
    sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      this.#applyVoucherDrivenCartBenefits();
      this.#applyVoucherDrivenCartPricing();
    });
    this.#observeFreeGiftCarouselLayout();
    this.#scheduleFreeGiftCarouselFilter();

    this.#runPostResponseCartIntegrity(
      parsedResponseText,
      didVoucherContextChange,
    );
  
  }

  /**
   * Runs the free-gift sanitizer and voucher reconcile off the cart we already have.
   * Both accept a known cart; neither was being given one, so every mutation paid for two
   * extra serial /cart.js round trips. A partial mutation response (e.g. the `/cart/add.js`
   * body) is rejected by `isFullCart` inside each, which falls back to fetching.
   * @param {Record<string, any>} parsedResponseText
   * @param {boolean} didVoucherContextChange
   */
  #runPostResponseCartIntegrity(parsedResponseText, didVoucherContextChange) {
    void this.#removeSuspiciousFreeGiftCartItems(parsedResponseText).then(
      (didRemove) => {
        if (!didVoucherContextChange) return undefined;

        // The sanitizer mutating the cart makes `parsedResponseText` stale.
        return this.#reconcileVoucherDrivenCartState(
          true,
          didRemove ? undefined : parsedResponseText,
        );
      },
    );
  }

  /**
   * Builds the sections payload expected by Shopify cart endpoints.
   * @returns {{sections: string, sections_url: string}}
   */
  #getSectionsPayload() {
    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll("cart-items-component").forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return {
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    };
  }

  /**
   * Reads the item count from rendered section HTML.
   * @param {string | undefined} sectionHtml
   * @returns {number}
   */
  #getItemCountFromSectionHtml(sectionHtml) {
    if (!sectionHtml) return 0;

    const newSectionHTML = new DOMParser().parseFromString(
      sectionHtml,
      "text/html",
    );
    const newCartHiddenItemCount = newSectionHTML.querySelector(
      '[ref="cartItemCount"]',
    )?.textContent;

    return newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
  }

  /**
   * Gets all one-time cart rows that can be converted to a subscription.
   * @returns {HTMLElement[]}
   */
  #getEligibleOneTimeLineRows() {
    return Array.from(
      this.querySelectorAll(
        '[data-key][data-is-subscribed="false"][data-subscription-eligible="true"][data-is-free-gift="false"]',
      ),
    ).filter((row) => row instanceof HTMLElement);
  }

  /**
   * Gets all subscribed cart rows with available selling plan data.
   * @returns {HTMLElement[]}
   */
  #getSubscribedLineRows() {
    return Array.from(
      this.querySelectorAll('[data-key][data-is-subscribed="true"]'),
    ).filter((row) => row instanceof HTMLElement);
  }

  /**
   * Gets a rendered cart row by Shopify cart line number instead of DOM index.
   * Discounted subscription carts can reorder rows, so relying on array position
   * causes quantity updates to target the wrong row.
   * @param {number | string} line
   * @returns {HTMLElement | null}
   */
  #getCartRowByLine(line) {
    const normalizedLine = Number(line || 0);
    if (!Number.isFinite(normalizedLine) || normalizedLine <= 0) return null;

    const row = this.querySelector(`[data-line="${normalizedLine}"]`);
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * Gets the quantity selector associated with a Shopify cart line number.
   * @param {number | string} line
   * @returns {HTMLElement | null}
   */
  #getQuantitySelectorByLine(line) {
    const row = this.#getCartRowByLine(line);
    if (!(row instanceof HTMLElement)) return null;

    const selector = row.querySelector(
      "cart-quantity-selector-component, quantity-selector-component",
    );

    return selector instanceof HTMLElement ? selector : null;
  }

  /**
   * Gets subscribed bundle rows that represent actual purchased items, not the free gift.
   * @returns {HTMLElement[]}
   */
  #getSubscriptionCoreRows() {
    return this.#getSubscribedLineRows().filter(
      (row) => row.dataset.isFreeGift !== "true",
    );
  }

  /**
   * Gets the currently selected free-gift row in the subscription bundle.
   * @returns {HTMLElement | null}
   */
  #getSelectedFreeGiftRow() {
    const row = this.querySelector(
      '[data-key][data-is-subscribed="true"][data-is-free-gift="true"]',
    );

    return row instanceof HTMLElement ? row : null;
  }

  /**
   * Gets the current bundle selling plan id from the first subscribed core row.
   * @returns {string}
   */
  #getCurrentBundleSellingPlanId() {
    const coreRow = this.#getSubscriptionCoreRows()[0];
    if (coreRow instanceof HTMLElement) {
      return this.#getLineData(coreRow).currentSellingPlanId;
    }

    const subscribedRow = this.#getSubscribedLineRows()[0];
    if (subscribedRow instanceof HTMLElement) {
      return this.#getLineData(subscribedRow).currentSellingPlanId;
    }

    return "";
  }

  /**
   * Gets the current bundle selling plan name from the frequency selector or subscribed rows.
   * @returns {string}
   */
  #getCurrentBundleSellingPlanName() {
    const frequencySelect = this.querySelector(
      "[data-cart-subscription-frequency]",
    );
    if (frequencySelect instanceof HTMLSelectElement && frequencySelect.value) {
      return frequencySelect.value.trim();
    }

    const coreRow = this.#getSubscriptionCoreRows()[0];
    if (coreRow instanceof HTMLElement) {
      return this.#getLineData(coreRow).currentSellingPlan;
    }

    const subscribedRow = this.#getSubscribedLineRows()[0];
    if (subscribedRow instanceof HTMLElement) {
      return this.#getLineData(subscribedRow).currentSellingPlan;
    }

    return "";
  }

  /**
   * Finds the selling plan that matches the current cart cadence.
   * Returns null when the cart is cadence-locked and this row does not support it.
   * @param {{sellingPlans: Array<{id: string, name: string}>}} lineData
   * @param {string} preferredPlanName
   * @returns {{id: string, name: string} | null}
   */
  #getPreferredSellingPlan(lineData, preferredPlanName = "") {
    const normalizedPreferredPlanName =
      this.#normalizePlanName(preferredPlanName);

    if (!normalizedPreferredPlanName) {
      return lineData.sellingPlans[0] || null;
    }

    return (
      lineData.sellingPlans.find(
        (plan) =>
          this.#normalizePlanName(plan.name) === normalizedPreferredPlanName,
      ) || null
    );
  }

  /**
   * Updates the previous/next button state for all free-gift carousels.
   */
  #syncFreeGiftCarouselNavState() {
    this.querySelectorAll("[data-cart-free-gift-carousel]").forEach(
      (carousel) => {
        if (!(carousel instanceof HTMLElement)) return;

        this.#updateFreeGiftCarouselNavState(carousel);

        if (carousel.dataset.navBound === "true") return;

        const syncState = () => this.#updateFreeGiftCarouselNavState(carousel);
        carousel.addEventListener("scroll", syncState, { passive: true });
        carousel.dataset.navBound = "true";
      },
    );
  }

  #syncStoredFreeGiftPreference() {
    const freeGiftRow = this.#getSelectedFreeGiftRow();
    if (!(freeGiftRow instanceof HTMLElement)) return;

    setStoredSelectedFreeGiftVariantId(freeGiftRow.dataset.variantId || "");
  }

  #handleVoucherContextReady = () => {
    this.#scheduleFreeGiftCarouselFilter();
    this.#applyVoucherDrivenCartBenefits();
    this.#applyVoucherDrivenCartPricing();
    void this.#reconcileVoucherDrivenCartState(true);
  };

  /**
   * @param {boolean} [force]
   * @param {Object} [knownCart] - Cart already in hand (page load); avoids a repeat fetch.
   */
  async #reconcileVoucherDrivenCartState(force = false, knownCart) {
    if (this.#voucherReconciliationPromise) {
      return this.#voucherReconciliationPromise;
    }

    this.#voucherReconciliationPromise = (async () => {
      try {
        let cart = isFullCart(knownCart)
          ? knownCart
          : await this.#fetchCartSnapshot();
        const voucherContext = this.#getActiveVoucherContext(cart);
        if (!voucherContext?.handle || !voucherContext.flTag) return;

        const reconciliationKey = `${voucherContext.handle}::${voucherContext.flTag}`;
        const desiredDiscountCodes = this.#normalizeDiscountCodeList(
          getVoucherDiscountCodesForHandle(voucherContext.handle),
        );
        const subscribedCartItems =
          this.#getSubscribedCartItemsFromSnapshot(cart);
        const subscriptionCoreItems =
          this.#getSubscriptionCoreItemsFromSnapshot(cart);
        const freeGiftItems = subscribedCartItems.filter((item) =>
          this.#isFreeGiftCartItem(item),
        );
        const currentFreeGiftItem = freeGiftItems[0] || null;

        if (
          !force &&
          this.#lastVoucherReconciliationKey === reconciliationKey &&
          freeGiftItems.length <= 1
        ) {
          return;
        }

        if (subscriptionCoreItems.length) {
          const fallbackSellingPlanId = this.#getCartItemSellingPlanId(
            subscriptionCoreItems[0],
          );
          const currentFreeGiftVariantId = String(
            currentFreeGiftItem?.variant_id || "",
          ).trim();
          const preferredFreeGiftSelection = getPreferredFreeGiftSelection({
            preferredVariantId: currentFreeGiftVariantId,
            sellingPlanId: fallbackSellingPlanId,
          });

          const nextPreferredGiftVariantId = String(
            preferredFreeGiftSelection?.variantId || "",
          ).trim();
          const shouldSyncFreeGift =
            freeGiftItems.length > 1 ||
            (!preferredFreeGiftSelection && currentFreeGiftVariantId) ||
            (preferredFreeGiftSelection &&
              currentFreeGiftVariantId !== nextPreferredGiftVariantId) ||
            (preferredFreeGiftSelection && !currentFreeGiftVariantId);

          if (shouldSyncFreeGift) {
            const desiredSelections = this.#mergeBundleSelections(
              [
                ...subscriptionCoreItems
                  .map((item) =>
                    this.#buildSelectionFromCartItem(
                      item,
                      fallbackSellingPlanId,
                    ),
                  )
                  .filter(Boolean),
                ...(preferredFreeGiftSelection
                  ? [preferredFreeGiftSelection]
                  : []),
              ].filter(Boolean),
            );

            await this.#syncSubscriptionBundle({
              desiredSelections,
              lineKeysToRemove: subscribedCartItems
                .map((item) => String(item?.key || "").trim())
                .filter(Boolean),
              loadingScope: "voucher-context",
            });

            cart = await this.#fetchCartSnapshot();
          }
        }

        const currentDiscountCodes = this.#getCartDiscountCodes(cart);
        if (
          !this.#areStringListsEqual(currentDiscountCodes, desiredDiscountCodes)
        ) {
          const updatedCart = await persistVoucherCartAttribute(
            voucherContext.flTag,
            voucherContext.voucherName,
            {
              discountCodes: desiredDiscountCodes,
              voucherHandle: voucherContext.handle,
              clearDiscount: true,
              sectionsPayload: this.#getSectionsPayload(),
              force: true,
              returnResponse: true,
            },
          );

          if (
            updatedCart &&
            typeof updatedCart === "object" &&
            !updatedCart.status &&
            !updatedCart.errors
          ) {
            this.#applyCartResponse(updatedCart);
          }
        }

        this.#lastVoucherReconciliationKey = reconciliationKey;
      } catch (error) {
        console.error(
          "Failed to reconcile cart state after voucher context changed.",
          error,
        );
      } finally {
        this.#voucherReconciliationPromise = null;
      }
    })();

    return this.#voucherReconciliationPromise;
  }

  #scheduleFreeGiftCarouselFilter() {
    this.#filterFreeGiftCarouselByVoucher();
    this.#syncStoredFreeGiftPreference();

    if (this.#freeGiftFilterFrame) {
      window.cancelAnimationFrame(this.#freeGiftFilterFrame);
    }

    this.#freeGiftFilterFrame = window.requestAnimationFrame(() => {
      this.#freeGiftFilterFrame = window.requestAnimationFrame(() => {
        this.#freeGiftFilterFrame = 0;
        this.#syncFreeGiftCarouselNavState();
      });
    });
  }

  #observeFreeGiftChanges() {
    this.#disconnectFreeGiftObserver();

    this.#freeGiftMutationObserver = new MutationObserver((mutations) => {
      const hasFreeGiftMutation = mutations.some((mutation) => {
        if (!(mutation.target instanceof HTMLElement)) return false;

        if (mutation.target.closest(".cart-items__free-gift")) {
          return true;
        }

        return [...mutation.addedNodes].some(
          (node) =>
            node instanceof HTMLElement &&
            (node.matches(".cart-items__free-gift") ||
              Boolean(node.querySelector?.(".cart-items__free-gift"))),
        );
      });

      if (hasFreeGiftMutation) {
        this.#observeFreeGiftCarouselLayout();
        this.#scheduleFreeGiftCarouselFilter();
      }
    });

    this.#freeGiftMutationObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  #disconnectFreeGiftObserver() {
    this.#freeGiftMutationObserver?.disconnect();
    this.#freeGiftMutationObserver = null;
  }

  #observeFreeGiftCarouselLayout() {
    this.#disconnectFreeGiftResizeObserver();

    this.#freeGiftResizeObserver = new ResizeObserver(() => {
      this.#scheduleFreeGiftCarouselFilter();
    });

    this.querySelectorAll("[data-cart-free-gift-carousel]").forEach(
      (carousel) => {
        if (!(carousel instanceof HTMLElement)) return;

        this.#freeGiftResizeObserver.observe(carousel);

        const wrapper = carousel.closest(".cart-items__free-gift");
        if (wrapper instanceof HTMLElement) {
          this.#freeGiftResizeObserver.observe(wrapper);
        }
      },
    );
  }

  #disconnectFreeGiftResizeObserver() {
    this.#freeGiftResizeObserver?.disconnect();
    this.#freeGiftResizeObserver = null;
  }

  #filterFreeGiftCarouselByVoucher() {
    const activeVoucherTag = this.#getActiveFreeGiftVoucherTag();

    this.querySelectorAll("[data-cart-free-gift-carousel]").forEach(
      (carousel) => {
        if (!(carousel instanceof HTMLElement)) return;

        const wrapper = carousel.closest(".cart-items__free-gift");
        const nav = wrapper?.querySelector(".cart-items__free-gift-nav");
        let visibleCardCount = 0;

        carousel
          .querySelectorAll(".cart-items__free-gift-card")
          .forEach((card) => {
            if (!(card instanceof HTMLElement)) return;

            const vouchers = this.#readFreeGiftVoucherList(card);
            const matchesVoucher = activeVoucherTag
              ? vouchers.includes(activeVoucherTag)
              : vouchers.length > 0;

            card.hidden = !matchesVoucher;
            if (matchesVoucher) visibleCardCount += 1;
          });

        carousel.scrollLeft = 0;

        if (nav instanceof HTMLElement) {
          nav.hidden = visibleCardCount <= 1;
        }
      },
    );
  }

  /**
   * Syncs prev/next controls for a free-gift carousel.
   * @param {HTMLElement} carousel
   */
  #updateFreeGiftCarouselNavState(carousel) {
    const wrapper = carousel.closest(".cart-items__free-gift");
    if (!(wrapper instanceof HTMLElement)) return;

    const prevButton = wrapper.querySelector(
      '[data-cart-free-gift-nav="prev"]',
    );
    const nextButton = wrapper.querySelector(
      '[data-cart-free-gift-nav="next"]',
    );

    if (
      !(prevButton instanceof HTMLButtonElement) ||
      !(nextButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    if (wrapper.querySelector(".cart-items__free-gift-nav")?.hidden) {
      prevButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const maxScrollLeft = Math.max(
      carousel.scrollWidth - carousel.clientWidth,
      0,
    );
    const canScroll = maxScrollLeft > 2;

    prevButton.disabled = !canScroll || carousel.scrollLeft <= 2;
    nextButton.disabled =
      !canScroll || carousel.scrollLeft >= maxScrollLeft - 2;
  }

  /**
   * Removes duplicate selling-plan labels from the shared cart frequency select.
   */
  #dedupeSubscriptionFrequencyOptions() {
    const select = this.querySelector("[data-cart-subscription-frequency]");
    if (!(select instanceof HTMLSelectElement)) return;

    const options = Array.from(select.options);
    if (options.length <= 1) return;

    const selectedValue = select.value;
    const seenOptionLabels = new Set();
    const fragment = document.createDocumentFragment();
    let didRemoveDuplicates = false;

    options.forEach((option) => {
      const normalizedLabel = (option.textContent || option.value || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (!normalizedLabel) return;

      if (seenOptionLabels.has(normalizedLabel)) {
        didRemoveDuplicates = true;
        return;
      }

      seenOptionLabels.add(normalizedLabel);
      fragment.appendChild(option.cloneNode(true));
    });

    if (!didRemoveDuplicates) return;

    select.replaceChildren(fragment);

    if (
      Array.from(select.options).some(
        (option) => option.value === selectedValue,
      )
    ) {
      select.value = selectedValue;
    } else if (select.options[0]) {
      select.options[0].selected = true;
    }
  }

  /**
   * Appends the currently selected free-gift selection to a bundle rebuild.
   * @param {Array<any>} baseSelections
   * @param {{
   *   excludeLineKey?: string,
   *   giftSelection?: Record<string, any> | null,
   *   sellingPlanId?: string,
   *   sellingPlanName?: string
   * }} [options]
   * @returns {Array<any>}
   */
  #appendSelectedFreeGiftSelection(baseSelections, options = {}) {
    const normalizedSelections = Array.isArray(baseSelections)
      ? baseSelections.filter(Boolean)
      : [];

    if (!normalizedSelections.length) return normalizedSelections;

    if (Object.prototype.hasOwnProperty.call(options, "giftSelection")) {
      return options.giftSelection
        ? [...normalizedSelections, options.giftSelection]
        : normalizedSelections;
    }

    const freeGiftRow = this.#getSelectedFreeGiftRow();
    if (!(freeGiftRow instanceof HTMLElement)) {
      const preferredFreeGiftSelection = getPreferredFreeGiftSelection({
        sellingPlanId:
          options.sellingPlanId || this.#getCurrentBundleSellingPlanId(),
        sellingPlanName:
          options.sellingPlanName || this.#getCurrentBundleSellingPlanName(),
      });

      return preferredFreeGiftSelection
        ? [...normalizedSelections, preferredFreeGiftSelection]
        : normalizedSelections;
    }

    if (
      options.excludeLineKey &&
      freeGiftRow.dataset.key === options.excludeLineKey
    ) {
      return normalizedSelections;
    }

    const freeGiftLineData = this.#getLineData(freeGiftRow);
    let nextSellingPlanId = options.sellingPlanId || "";

    if (options.sellingPlanName) {
      const preferredSellingPlanById = freeGiftLineData.sellingPlans.find(
        (plan) => String(plan?.id || "").trim() === nextSellingPlanId,
      );
      const preferredSellingPlan =
        preferredSellingPlanById ||
        this.#getPreferredSellingPlan(
          freeGiftLineData,
          options.sellingPlanName,
        );

      if (!preferredSellingPlan) {
        console.error(
          "Unable to keep the free gift in the subscription bundle because no selling plan matched the current cart cadence.",
          {
            freeGiftLineData,
            sellingPlanName: options.sellingPlanName,
          },
        );
        return normalizedSelections;
      }

      nextSellingPlanId = preferredSellingPlan.id;
    }

    const freeGiftSelection = this.#createBundleSelection(freeGiftLineData, {
      sellingPlanId: nextSellingPlanId || undefined,
    });

    return freeGiftSelection
      ? [...normalizedSelections, freeGiftSelection]
      : normalizedSelections;
  }

  /**
   * Builds a free-gift bundle selection from the cart gift picker.
   * @param {HTMLButtonElement | HTMLSelectElement} control
   * @param {{sellingPlanId?: string, sellingPlanName?: string}} [overrides]
   * @returns {Record<string, any> | null}
   */
  #buildFreeGiftSelectionFromControl(control, overrides = {}) {
    let productId = "";
    let variantId = "";
    let collectionId = "";
    let collectionIds = [];
    let sellingPlans = [];

    if (control instanceof HTMLButtonElement) {
      productId = control.dataset.productId || "";
      variantId = control.dataset.variantId || control.value;
      collectionId = control.dataset.collectionId || "";
      collectionIds = this.#parseCollectionIds(
        this.#parseJsonScript(
          control,
          "[data-cart-free-gift-product-collections]",
        ),
      );
      sellingPlans =
        this.#parseJsonScript(
          control,
          "[data-cart-free-gift-selling-plan-options]",
        ) || [];
    } else if (control instanceof HTMLSelectElement) {
      const selectedOption = control.selectedOptions[0];
      if (!(selectedOption instanceof HTMLOptionElement)) return null;

      productId = selectedOption.dataset.productId || "";
      variantId = selectedOption.dataset.variantId || selectedOption.value;
      collectionId = selectedOption.dataset.collectionId || "";
      collectionIds = this.#parseCollectionIds(
        this.#parseJsonScript(
          selectedOption,
          "[data-cart-free-gift-product-collections]",
        ),
      );
      sellingPlans =
        this.#parseJsonScript(
          selectedOption,
          "[data-cart-free-gift-selling-plan-options]",
        ) || [];
    } else {
      return null;
    }

    const preferredPlanName =
      overrides.sellingPlanName || this.#getCurrentBundleSellingPlanName();
    const preferredPlanId =
      overrides.sellingPlanId || this.#getCurrentBundleSellingPlanId();
    const preferredSellingPlanById = sellingPlans.find(
      (plan) =>
        String(plan?.id || "").trim() === String(preferredPlanId || "").trim(),
    );
    const preferredSellingPlan =
      preferredSellingPlanById ||
      this.#getPreferredSellingPlan({ sellingPlans }, preferredPlanName);

    if (!preferredSellingPlan) {
      return null;
    }

    const sellingPlanId = preferredSellingPlan.id;
    const nextCollectionId = collectionIds[0] || collectionId;

    if (!productId || !variantId || !sellingPlanId || !nextCollectionId) {
      return null;
    }

    return this.#createBundleSelection(
      {
        productId,
        variantId: Number(variantId),
        quantity: 1,
        properties: {},
        collectionIds,
        currentSellingPlanId: String(sellingPlanId),
        currentSellingPlan: preferredSellingPlan?.name || preferredPlanName,
        isFreeGift: true,
        freeGiftCollectionId: nextCollectionId,
        sellingPlans,
      },
      {
        collectionId: nextCollectionId,
        quantity: 1,
        sellingPlanId,
      },
    );
  }

  /**
   * Builds a Recharge bundle child selection from line data.
   * @param {{
   *   productId: string,
   *   variantId: number,
   *   quantity: number,
   *   properties: Record<string, string>,
   *   collectionIds: string[],
   *   currentSellingPlanId: string,
   *   isFreeGift?: boolean,
   *   freeGiftCollectionId?: string
   * }} lineData
   * @param {{quantity?: number, sellingPlanId?: string, collectionId?: string}} [overrides]
   * @returns {{
   *   productId: string,
   *   variantId: string,
   *   quantity: number,
   *   sellingPlan: string,
   *   collectionId: string,
   *   properties: Record<string, string>,
   *   collectionIds: string[],
   *   isFreeGift: boolean
   * } | null}
   */
  #createBundleSelection(lineData, overrides = {}) {
    const quantity = Number(overrides.quantity ?? lineData.quantity ?? 0);
    const sellingPlanId =
      overrides.sellingPlanId ?? lineData.currentSellingPlanId;
    const collectionId =
      overrides.collectionId ||
      lineData.properties._rc_bundle_collection_id ||
      (lineData.isFreeGift ? lineData.freeGiftCollectionId : "") ||
      lineData.collectionIds[0] ||
      "";

    if (
      !lineData.productId ||
      !lineData.variantId ||
      quantity <= 0 ||
      !sellingPlanId ||
      !collectionId
    ) {
      return null;
    }

    return {
      productId: lineData.productId,
      variantId: String(lineData.variantId),
      quantity,
      sellingPlan: String(sellingPlanId),
      collectionId: String(collectionId),
      isFreeGift: Boolean(lineData.isFreeGift),
      properties: lineData.properties,
      collectionIds: lineData.collectionIds,
    };
  }

  /**
   * Builds a one-time cart item from an existing subscribed line.
   * @param {{
   *   variantId: number,
   *   quantity: number,
   *   properties: Record<string, string>,
   *   requiresSellingPlan?: boolean
   * }} lineData
   * @returns {{id: number, quantity: number} | null}
   */
  #buildOneTimeCartItem(lineData) {
    if (!lineData.variantId || lineData.quantity <= 0) return null;
    if (lineData.requiresSellingPlan) return null;

    return {
      id: lineData.variantId,
      quantity: lineData.quantity,
    };
  }

  /**
   * Parses line-item data embedded in the DOM.
   * @param {HTMLElement} row
   * @returns {{
   *   key: string,
   *   productId: string,
   *   variantId: number,
   *   quantity: number,
   *   properties: Record<string, string>,
   *   collectionIds: string[],
   *   isFreeGift: boolean,
   *   freeGiftCollectionId: string,
   *   requiresSellingPlan: boolean,
   *   sellingPlans: Array<{id: string, name: string}>,
   *   currentSellingPlanId: string,
   *   currentSellingPlan: string
   * }}
   */
  #getLineData(row) {
    const properties =
      this.#parseJsonScript(row, "[data-cart-line-properties]") || {};
    const collectionIds = this.#parseCollectionIds(
      this.#parseJsonScript(row, "[data-cart-product-collections]") ||
        properties._gc_product_collection_ids,
    );
    const isFreeGift =
      row.dataset.isFreeGift === "true" ||
      properties._gc_subscription_free_gift === "true";

    return {
      key: row.dataset.key || "",
      productId: row.dataset.productId || "",
      variantId: Number(row.dataset.variantId || 0),
      quantity: Number(row.dataset.quantity || 0),
      properties,
      collectionIds,
      isFreeGift,
      freeGiftCollectionId: row.dataset.freeGiftCollectionId || "",
      requiresSellingPlan: row.dataset.requiresSellingPlan === "true",
      sellingPlans:
        this.#parseJsonScript(row, "[data-cart-selling-plan-options]") || [],
      currentSellingPlanId: row.dataset.currentSellingPlanId || "",
      currentSellingPlan: row.dataset.currentSellingPlan || "",
    };
  }

  /**
   * Reads JSON content from a script tag inside a row.
   * @template T
   * @param {HTMLElement} row
   * @param {string} selector
   * @returns {T | null}
   */
  #parseJsonScript(row, selector) {
    const script = row.querySelector(selector);
    if (!(script instanceof HTMLScriptElement) || !script.textContent)
      return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
